import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { IBattleshipGameState } from '@skillarea/game-battleship';
import { GameLayout, type IPlayer } from '@skillarea/game-layout';

import { usePhaserGameLoader } from '../use-phaser-game-loader';
import type { TGamePlayer } from '../types';

import { getBattleshipShotSoundFromStateDiff } from './battleship-shot-sound-from-state';
import styles from './BattleshipGame.module.scss';

import { GameLoadingBlock } from '~/components/Matchmaking/screens/GameScreen';
import {
  playGameStartSound,
  playHitSound,
  playMoveSound,
  playShipSunkSound,
} from '~/lib/game-sounds';
import { useGameLayoutLabels } from '~/hooks/useGameLayoutLabels';
import { useLocalizedGameName } from '~/hooks/useLocalizedGame';

export interface IBattleshipGameProps {
  state: IBattleshipGameState;
  playerSlot: 0 | 1;
  player1: TGamePlayer;
  player2: TGamePlayer;
  player1TimeLeftMs: number;
  player2TimeLeftMs: number;
  turnDurationMs: number;
  isGameOver: boolean;
  winner: 0 | 1 | null;
  onShoot?: (row: number, col: number) => void;
  onSurrender?: () => void;
  rulesContent?: string;
}

export const BattleshipGame: FC<IBattleshipGameProps> = ({
  isGameOver,
  onShoot,
  onSurrender,
  player1: player1Info,
  player1TimeLeftMs,
  player2: player2Info,
  player2TimeLeftMs,
  playerSlot,
  rulesContent = '',
  state,
  turnDurationMs,
  winner,
}) => {
  const gameLayoutLabels = useGameLayoutLabels();
  const gameName = useLocalizedGameName('battleship', 'Battleship');
  const containerRef = useRef<globalThis.HTMLDivElement>(null);

  type TBattleshipPhaserGame = {
    updateState: (state: IBattleshipGameState, playerSlot: 0 | 1) => void;
    destroy: () => void;
  };
  type TBattleshipPhaserGameConstructor = new (args: {
    parent: globalThis.HTMLDivElement;
    onShoot?: (row: number, col: number) => void;
  }) => TBattleshipPhaserGame;

  const gameRef = useRef<TBattleshipPhaserGame | null>(null);
  const prevStateForSoundRef = useRef<typeof state | null>(null);
  const skipShotSoundRef = useRef(true);
  const onShootRef = useRef(onShoot);
  const stateRef = useRef(state);
  const playerSlotRef = useRef(playerSlot);
  onShootRef.current = onShoot;
  stateRef.current = state;
  playerSlotRef.current = playerSlot;

  const loadGame = useCallback(
    async (
      parent: globalThis.HTMLDivElement,
    ): Promise<TBattleshipPhaserGame> => {
      const mod = await import('@skillarea/game-battleship-phaser');
      const Game =
        mod.BattleshipPhaserGame as unknown as TBattleshipPhaserGameConstructor;
      const game = new Game({
        parent,
        onShoot: (row, col) => {
          onShootRef.current?.(row, col);
        },
      });
      const withReady = game as {
        whenReady?: () => Promise<void>;
      };
      if (withReady.whenReady) await withReady.whenReady();
      return game;
    },
    [],
  );

  const player1: IPlayer = {
    id: 'player1',
    name: player1Info.name,
    avatarUrl: player1Info.avatarUrl,
    symbol: 'P1',
  };

  const player2: IPlayer = {
    id: 'player2',
    name: player2Info.name,
    avatarUrl: player2Info.avatarUrl,
    symbol: 'P2',
  };

  const activePlayerId =
    isGameOver || winner !== null
      ? null
      : state.currentPlayer === playerSlot
        ? 'player1'
        : 'player2';

  const { isReady } = usePhaserGameLoader({
    containerRef,
    gameRef,
    loadGame,
    onLoaded: (game) => {
      playGameStartSound();
      game.updateState(stateRef.current, playerSlotRef.current);
    },
  });

  useEffect(() => {
    if (gameRef.current) {
      gameRef.current.updateState(state, playerSlot);
    }
  }, [state, playerSlot]);

  useEffect(() => {
    if (skipShotSoundRef.current) {
      skipShotSoundRef.current = false;
      prevStateForSoundRef.current = state;
      return;
    }
    const prev = prevStateForSoundRef.current;
    prevStateForSoundRef.current = state;
    if (!prev) {
      return;
    }
    const shotSound = getBattleshipShotSoundFromStateDiff(prev, state);
    if (shotSound === null) {
      return;
    }
    if (shotSound === 'sunk') {
      playShipSunkSound();
      return;
    }
    if (shotSound === 'hit') {
      playHitSound();
      return;
    }
    playMoveSound();
  }, [state]);

  useEffect(() => {
    if (!isReady || !gameRef.current) return;
    const id = window.requestAnimationFrame(() => {
      if (gameRef.current) {
        gameRef.current.updateState(stateRef.current, playerSlotRef.current);
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [isReady]);

  const gameContent = (
    <div className={styles.gameWrapper}>
      <div className={styles.gameContainer} ref={containerRef} />
      {!isReady ? <GameLoadingBlock className={styles.gameLoading} /> : null}
    </div>
  );

  return (
    <GameLayout
      activePlayerId={activePlayerId}
      fullWidthMobilePlayersPreset="compactRow"
      gameContent={gameContent}
      gameName={gameName}
      labels={gameLayoutLabels}
      layoutVariant="fullWidth"
      onSurrender={onSurrender}
      player1={player1}
      player1TimeLeftMs={player1TimeLeftMs}
      player2={player2}
      player2TimeLeftMs={player2TimeLeftMs}
      rulesContent={rulesContent}
      turnDurationMs={turnDurationMs}
    />
  );
};
